import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import {useLocation, useNavigate} from 'react-router';
import {getUsersByIds} from '../../../firebase/firestore/users';
import {useSession} from '../../../firebase/UserProvider';
import PersonCell, {indexUsersById, userDisplayName} from '../components/PersonCell';
import openCollectionIir from '../viewer/openCollectionIir';
import {listCollectionIirs} from '../collectionTaskApi';

const IIR_STATUSES = [
    {value: 'draft', label: 'Draft'},
    {value: 'submitted', label: 'Submitted'},
    {value: 'approved', label: 'Approved'},
];

function statusChipProps(workflowStatus) {
    if (workflowStatus === 'approved') {
        return {label: 'Approved', color: 'success', variant: 'filled'};
    }
    if (workflowStatus === 'submitted') {
        return {label: 'Submitted', color: 'info', variant: 'filled'};
    }
    return {label: 'Draft', color: 'default', variant: 'outlined'};
}

export default function ListCollectionIirs() {
    const navigate = useNavigate();
    const location = useLocation();
    const {user} = useSession() || {};
    const [items, setItems] = useState([]);
    const [people, setPeople] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [q, setQ] = useState('');
    const [status, setStatus] = useState('');

    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const list = await listCollectionIirs();
            setItems(list);
            const ids = [...new Set(list.map((r) => r.ownerUserId).filter(Boolean))];
            if (ids.length) {
                setPeople(indexUsersById(await getUsersByIds(ids)));
            } else {
                setPeople({});
            }
        } catch (e) {
            setError(e?.message || 'Failed to load IIRs');
            setItems([]);
            setPeople({});
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        reload();
    }, [reload]);

    const rows = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return items.filter((row) => {
            const workflowStatus = row.workflowStatus || row.status || 'draft';
            if (status && workflowStatus !== status) return false;
            if (!needle) return true;
            const authorName = userDisplayName(people[row.ownerUserId]) || row.ownerUserId || '';
            const hay = [
                row.iirNumber,
                row.title,
                row.classification,
                row.ctNumber,
                row.taskTitle,
                row.collectionTaskId,
                row.reportId,
                row.id,
                workflowStatus,
                authorName,
            ].filter(Boolean).join(' ').toLowerCase();
            return hay.includes(needle);
        });
    }, [items, people, q, status]);

    return (
        <Box p={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">Intelligence Information Reports</Typography>
            </Stack>

            <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} mb={2}>
                <TextField
                    size="small"
                    label="Search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    sx={{minWidth: 240}}
                />
                <FormControl size="small" sx={{minWidth: 180}}>
                    <InputLabel id="iir-status-filter-label">Status</InputLabel>
                    <Select
                        labelId="iir-status-filter-label"
                        id="iir-status-filter"
                        label="Status"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                    >
                        <MenuItem value="">All</MenuItem>
                        {IIR_STATUSES.map((s) => (
                            <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button variant="outlined" onClick={reload}>Refresh</Button>
            </Stack>

            {loading && <Stack alignItems="center" py={4}><CircularProgress/></Stack>}
            {error && <Typography color="error" mb={2}>{error}</Typography>}

            {!loading && (
                <Paper variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell sx={{width: '36%'}}>Title</TableCell>
                                <TableCell sx={{whiteSpace: 'nowrap'}}>Status</TableCell>
                                <TableCell sx={{whiteSpace: 'nowrap'}}>Classification</TableCell>
                                <TableCell>Author</TableCell>
                                <TableCell>Updated</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Typography color="text.secondary" py={2} align="center">
                                            No IIRs found.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {rows.map((row) => {
                                const status = row.workflowStatus || row.status || 'draft';
                                const chip = statusChipProps(status);
                                return (
                                    <TableRow
                                        key={row.id}
                                        hover
                                        sx={{cursor: 'pointer'}}
                                        // Author+Draft → editor; otherwise C2 viewer (returnTo
                                        // restores this list).
                                        onClick={() => openCollectionIir({
                                            iir: row,
                                            userId: user?.uid,
                                            navigate,
                                            location,
                                        })}
                                    >
                                        <TableCell sx={{fontFamily: 'monospace', whiteSpace: 'nowrap'}}>
                                            {row.iirNumber || '—'}
                                        </TableCell>
                                        <TableCell sx={{
                                            maxWidth: 360,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {row.title}
                                        </TableCell>
                                        <TableCell sx={{whiteSpace: 'nowrap'}}>
                                            <Chip
                                                size="small"
                                                label={chip.label}
                                                color={chip.color}
                                                variant={chip.variant}
                                            />
                                        </TableCell>
                                        <TableCell sx={{whiteSpace: 'nowrap'}}>
                                            {row.classification || '—'}
                                        </TableCell>
                                        <TableCell
                                            onClick={(e) => e.stopPropagation()}
                                            sx={{maxWidth: 220}}
                                        >
                                            <PersonCell userId={row.ownerUserId} profile={people[row.ownerUserId]}/>
                                        </TableCell>
                                        <TableCell>
                                            {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Paper>
            )}
        </Box>
    );
}
